import isEmpty from "lodash/isEmpty";
import { useCallback, useEffect, useState } from "react";
import { useReactiveVar } from "@apollo/client/react";
import { makeVar, ReactiveVar } from "@apollo/client/cache";

export const LOCAL_STORAGE_KEYS = {
  COACH: "dream_coach" as const,
  COACH_STEPS: "dream_coach_steps" as const,
  COACH_TOUR_SEEN: "dream_coach_tour_seen" as const,
  MAGIC_LINK_METHOD: "magic_link_method" as const,
  MAGIC_LINK_EMAIL: "magic_link_email" as const,
  MAGIC_LINK_PHONE: "magic_link_phone" as const,
  MAGIC_LINK_CODE: "magic_link_code" as const,
  MAGIC_LINK_CODE_SENT: "magic_link_code_sent" as const,
  DREAM_SEARCH_BASE_RESULTS: "dream_search_base_results" as const,
  AFFILIATE_SLUG: "affiliate_slug" as const,
  AFFILIATE_FIRST_ENCOUNTERED_AT: "affiliate_first_encountered_at" as const,
  AFFILIATE_EXTERNAL_ID: "affiliate_external_id" as const,
  EVERFLOW_TRANSACTION_ID: "everflow_transaction_id" as const,
  VERIFICATION_CODE_REQUESTED: "verification_code_requested" as const,
  JOBS_API_KEY: "jobs_api_key" as const,
  // Exposed for unit test
  TEST: "test1" as const,
};

const parseLocalStorageValue = (key: Key) => {
  const rawValue = window.localStorage.getItem(key);
  try {
    // Unsetting value where the string "undefined" has somehow
    // been set in localStorage.
    if (rawValue === "undefined") {
      return "";
    }
    if (
      rawValue !== null &&
      (rawValue.startsWith?.("{") ||
        rawValue.startsWith?.("[") ||
        ["true", "false", "null"].includes(rawValue))
    ) {
      return JSON.parse(<string>rawValue);
    } else {
      return rawValue ?? undefined;
    }
  } catch (error) {
    return rawValue;
  }
};

const reactiveLocalStorage: Record<Key, ReactiveVar<undefined>> = {
  [LOCAL_STORAGE_KEYS.COACH]: makeVar(undefined),
  [LOCAL_STORAGE_KEYS.COACH_STEPS]: makeVar(undefined),
  [LOCAL_STORAGE_KEYS.COACH_TOUR_SEEN]: makeVar(undefined),
  [LOCAL_STORAGE_KEYS.MAGIC_LINK_METHOD]: makeVar(undefined),
  [LOCAL_STORAGE_KEYS.MAGIC_LINK_EMAIL]: makeVar(undefined),
  [LOCAL_STORAGE_KEYS.MAGIC_LINK_PHONE]: makeVar(undefined),
  [LOCAL_STORAGE_KEYS.MAGIC_LINK_CODE]: makeVar(undefined),
  [LOCAL_STORAGE_KEYS.MAGIC_LINK_CODE_SENT]: makeVar(undefined),
  [LOCAL_STORAGE_KEYS.DREAM_SEARCH_BASE_RESULTS]: makeVar(undefined),
  [LOCAL_STORAGE_KEYS.AFFILIATE_SLUG]: makeVar(undefined),
  [LOCAL_STORAGE_KEYS.AFFILIATE_FIRST_ENCOUNTERED_AT]: makeVar(undefined),
  [LOCAL_STORAGE_KEYS.AFFILIATE_EXTERNAL_ID]: makeVar(undefined),
  [LOCAL_STORAGE_KEYS.EVERFLOW_TRANSACTION_ID]: makeVar(undefined),
  [LOCAL_STORAGE_KEYS.VERIFICATION_CODE_REQUESTED]: makeVar(undefined),
  [LOCAL_STORAGE_KEYS.JOBS_API_KEY]: makeVar(undefined),
  // Exposed for unit test
  [LOCAL_STORAGE_KEYS.TEST]: makeVar(undefined),
};

type Key = (typeof LOCAL_STORAGE_KEYS)[keyof typeof LOCAL_STORAGE_KEYS];
/**
 * Same as useState, except that it retrieves and updates <key> in localStorage
 * if in a browser (i.e. localStorage is defined).
 *
 * @param key Key used in localStorage
 * @param initialValue Initializer object
 * @returns Signature tuple of the following:
 *    Value
 *    Value setter
 *    LocalStorage item remover
 */
type ValueOptions =
  | string
  | number
  | boolean
  | Record<string, unknown>
  | unknown[]
  | undefined
  | null;

export function useLocalStorage<Value extends ValueOptions, ReturnValue = Value | undefined>(
  key: Key,
  initialValue?: never
): [ReturnValue, React.Dispatch<React.SetStateAction<ReturnValue>>, () => void];

export function useLocalStorage<Value extends NonNullable<ValueOptions>, ReturnValue = Value>(
  key: Key,
  initialValue: Value
): [ReturnValue, React.Dispatch<React.SetStateAction<ReturnValue>>, () => void];

export function useLocalStorage<Value extends ValueOptions, ReturnValue = Value>(
  key: Key,
  initialValue: Value
) {
  const reactiveVar = reactiveLocalStorage[key] as unknown as ReactiveVar<ReturnValue>;
  const reactiveValue = useReactiveVar<ReturnValue>(reactiveVar);
  // Only set from LS if LS is available & the key exists
  const [value, setValue] = useState<ReturnValue>(() => {
    try {
      const initialState =
        typeof window !== "undefined" && window.localStorage?.getItem(key) !== undefined
          ? parseLocalStorageValue(key)
          : initialValue;
      if (initialState !== undefined && reactiveValue === undefined) {
        reactiveVar(initialState);
      }
      return initialState;
    } catch (error) {
      if (error.code === 18) {
        alert(
          "Dreambound.com requires cookies to be enabled. To use Dreambound, go to your cookie settings and either allow all cookies or allow cookies for dreambound.com"
        );
      } else {
        throw error;
      }
    }
  });

  const removeItem = useCallback(() => {
    window.localStorage?.removeItem(key);
    setValue(initialValue as unknown as ReturnValue);
    reactiveVar(initialValue as unknown as ReturnValue);
  }, [initialValue, key, reactiveVar]);

  const setValueAndLocalStorage = useCallback(
    (setStateAction: React.SetStateAction<ReturnValue>) => {
      setValue((prevState) => {
        let newValue: ReturnValue;
        if (typeof setStateAction === "function") {
          newValue = (setStateAction as (prevState: ReturnValue) => ReturnValue)(prevState);
        } else {
          newValue = setStateAction as ReturnValue;
        }

        const isInBrowser = typeof window !== undefined;
        if (isInBrowser) {
          // Unsetting value where the string "undefined" has somehow
          // been set in localStorage.
          if (newValue === undefined || newValue === "undefined") {
            window.localStorage?.setItem(key, "");
          } else {
            if (typeof newValue === "string") {
              window.localStorage?.setItem(key, newValue);
            } else {
              window.localStorage?.setItem(key, JSON.stringify(newValue));
            }
          }
          reactiveVar(newValue);
        }
        return newValue;
      });
    },
    [key, reactiveVar]
  );

  useEffect(() => {
    /**
     * This creates a better reactivity to localStorage values. Otherwise,
     * a localStorage value can change via setValueAndLocalStorage on another
     * page, and another instance of useLocalStorage will never know
     */
    const lsValue = window.localStorage.getItem(key);
    const parsedValue = parseLocalStorageValue(key);
    if (parsedValue !== undefined) {
      if (
        (parsedValue === null && parsedValue !== value) ||
        (typeof parsedValue !== "object" && parsedValue !== undefined && parsedValue !== value) ||
        (typeof parsedValue === "object" &&
          !isEmpty(parsedValue) &&
          lsValue !== JSON.stringify(value))
      ) {
        setValueAndLocalStorage(parsedValue);
      } else if (
        parsedValue === undefined ||
        (typeof parsedValue === "object" && parsedValue !== null && isEmpty(parsedValue))
      ) {
        removeItem();
      }
    }
  }, [key, reactiveVar, removeItem, setValueAndLocalStorage, value]);

  useEffect(() => {
    /**
     * If the reactiveVar changes due to another component, we need to update
     * the value in this component
     */
    setValue(reactiveVar());
  }, [reactiveValue, reactiveVar]);

  return [value, setValueAndLocalStorage, removeItem];
}
